import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import "../../styles/popupStyle.css";
import {
  validateString,
  validationLength,
  validationType,
} from "../../actions/validations";
import {
  reserveRolesFieldErrors,
  reserveRolesFieldHelperText,
} from "../../../constants/reserveRolesValidationHelperText";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import InfoIcon from "@mui/icons-material/Info";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues } from "../../../constants/Types";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import { ReservePropertyEnum } from "../../../constants/NavbarList";
import {
  reserveListAvailableHeadingTooltip,
  reserveRolesTooltip,
} from "../../../constants/ReserveTooltipInformation";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import AlertComponent from "../../subcomponents/AlertComponent";
import { textStyle } from "../../styles/Style";
import useScrollToTop from "../../hooks/useScrollToTop";

const ReserveRoles = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [roleCode, setRoleCode] = useState<string | null>(null);
  const [roleName, setRoleName] = useState<string | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const [codeError, setCodeError] = useState<boolean>(false);
  const [nameError, setNameError] = useState<boolean>(false);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [roleInformation, setRoleInformation] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const payload = {
    "Role Code": roleCode,
    "Role Name": roleName,
    Active: active,
  };

  const validateInput = () => {
    setCodeError(false);
    setNameError(false);

    let ok = true;
    const codeTrim = (roleCode ?? "").trim();
    const nameTrim = (roleName ?? "").trim();

    if (!codeTrim) {
      setCodeError(true);
      ok = false;
    } else if (
      !validateString(codeTrim, validationLength.roleCode, validationType.CODE)
    ) {
      setCodeError(true);
      ok = false;
    }

    if (!nameTrim) {
      setNameError(true);
      ok = false;
    } else if (
      !validateString(nameTrim, validationLength.roleName, validationType.SPACE)
    ) {
      setNameError(true);
      ok = false;
    }

    if (!ok) {
      if (!codeTrim) {
        setAlertMessage("Role code is mandatory");
      } else if (!nameTrim) {
        setAlertMessage("Role name is mandatory");
      } else {
        setAlertMessage("Please correct the highlighted fields");
      }
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    return true;
  };

  const editRole = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    if (list == null) {
      setRoleCode(null);
      setRoleName(null);
      setActive(true);
      setCodeError(false);
      setNameError(false);
      setEditClicked(false);
      return;
    }
    setRoleCode(list?.["Role Code"] ?? null);
    setRoleName(list?.["Role Name"] ?? null);
    setActive(list?.Active ?? true);
    setCodeError(false);
    setNameError(false);
  };

  const deleteRole = (id: number) => {
    setRoleInformation(deleteItem(roleInformation ?? [], id));
  };

  const updateRole = () => {
    if (!validateInput()) return false;
    if (
      checkDuplicate(
        (roleInformation ?? []).filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        "Role Code",
        roleCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setCodeError(false);
    setNameError(false);
    setRoleInformation((prev: any) =>
      (Array.isArray(prev) ? prev : []).map((item: any) =>
        String(item.id) === String(selectedId) ? { ...item, ...payload } : item,
      ),
    );
    editRole(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${ReservePropertyEnum.roles} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addRole = () => {
    if (!validateInput()) {
      return;
    }
    if (checkDuplicate(roleInformation ?? [], "Role Code", roleCode)) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setRoleInformation((prev: any) =>
      updateData(
        Array.isArray(prev) && prev.length
          ? [
              ...prev,
              {
                id: prev.length + 1,
                ...payload,
              },
            ]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editRole(null);
    setEditClicked(false);
    setSelectedId(null);
  };

  useEffect(() => {
    setRoleInformation(Array.isArray(fieldValues) ? fieldValues : []);
  }, []);

  useEffect(() => {
    data(roleInformation);
    editClicked &&
      setSelectedId(checkData(roleInformation, selectedId) ? selectedId : null);
  }, [roleInformation]);

  const roleCodeHelperText = codeError
    ? !(roleCode?.trim() ?? "")
      ? reserveRolesFieldErrors.required
      : reserveRolesFieldErrors.roleCode
    : reserveRolesFieldHelperText.roleCode;

  const roleNameHelperText = nameError
    ? !(roleName?.trim() ?? "")
      ? reserveRolesFieldErrors.required
      : reserveRolesFieldErrors.roleName
    : reserveRolesFieldHelperText.roleName;

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          gap="10px"
          alignItems="center"
          justifyContent="space-between"
          mb={4}
        >
          <Typography variant="h6" component="div" sx={textStyle}>
            {ReservePropertyEnum.roles}
            <Tooltip title={tooltipComponent(reserveRolesTooltip.header)}>
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px" justifyContent="space-between">
            {editClicked && (
              <ClearButton
                click={() => {
                  editRole(null);
                  setSelectedId(null);
                  setEditClicked(false);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton
                click={() => {
                  editRole(lastUpdatedRecord);
                }}
              />
            )}
          </Stack>
        </Stack>

        <Stack direction="column" width="100%" gap="5px">
          <FormLabel component="legend">
            Role Code
            <Tooltip title={tooltipComponent(reserveRolesTooltip.roleCode)}>
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <TextField
            id="reserveRoleCode"
            variant="outlined"
            hiddenLabel
            placeholder="Role code"
            helperText={roleCodeHelperText}
            error={codeError}
            value={roleCode ?? ""}
            fullWidth
            onChange={(e: any) => {
              setRoleCode(e.target.value);
              setCodeError(false);
            }}
            inputProps={{ "aria-label": "Role code" }}
          />
        </Stack>

        <Stack direction="column" width="100%" gap="5px">
          <FormLabel component="legend">
            Role Name
            <Tooltip title={tooltipComponent(reserveRolesTooltip.roleName)}>
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <TextField
            id="reserveRoleName"
            variant="outlined"
            hiddenLabel
            placeholder="Role name"
            value={roleName ?? ""}
            helperText={roleNameHelperText}
            error={nameError}
            fullWidth
            onChange={(e: any) => {
              setRoleName(e.target.value);
              setNameError(false);
            }}
            inputProps={{ "aria-label": "Role name" }}
          />
        </Stack>

        <Stack direction="column" width="100%" gap="5px">
          <FormLabel component="legend">
            Active?
            <Tooltip title={tooltipComponent(reserveRolesTooltip.active)}>
              <InfoIcon fontSize="small" />
            </Tooltip>
          </FormLabel>
          <Stack
            className="form-checkbox-row form-two-col-row"
            direction="row"
            alignItems="center"
            gap={1}
          >
            <Switch
              checked={active}
              onChange={() => setActive(!active)}
              inputProps={{ "aria-label": "Active role" }}
            />
            <Typography variant="body2" color="text.secondary">
              {active ? "Yes" : "No"}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction="row" width="100%" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateRole() : addRole())}
            id="addReserveRole"
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD}{" "}
            {ReservePropertyEnum.roles}
          </Button>
        </Stack>

        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography
            variant="h6"
            component="div"
            sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
          >
            {ReservePropertyEnum.roles} available
            <Tooltip
              title={tooltipComponent(reserveListAvailableHeadingTooltip)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="column" gap="10px">
            {Array.isArray(roleInformation) && roleInformation.length > 0 ? (
              <DraggableItemList
                itemData={roleInformation}
                onOrderUpdate={(order) => setRoleInformation(order)}
                onEdit={(info) => {
                  editRole(info);
                  scrollToTop();
                }}
                onDelete={(id) => deleteRole(id)}
                code="Role Code"
                name="Role Name"
              />
            ) : (
              <Typography variant="body2">
                No {ReservePropertyEnum.roles} information available
              </Typography>
            )}
          </Stack>
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default ReserveRoles;
